import { useMutation } from '@tanstack/react-query';
import { findIndex } from 'lodash';
import { produce } from 'immer';
import {
  guideWriteCommandApi,
  placeAssetInVideoWidget,
  toggleVideoWidgetSettings,
} from '../../helpers/api/guidelinesApi';
import { Page, StyleSheet, WIDGET_TYPE } from '../../types';
import { handleMutationSuccess, updateGuideVersion } from '../helpers';
import { GUIDE_KEYS } from '../../queries/guides';
import { queryClient } from '../../constants/app';
import { useGuideStore } from '../../stores/guideStore';
import { Command } from '../../types/commands';
import { captureEvent } from '@sentry/react';
import { editVideoWidgetStyleCommand } from '../../helpers/api/commands/widgets/videoWidgetCommands';
import { optimisticallyUpdateWidgetStyle } from './collections';

export enum VideoSettings {
  controls = 'controls',
  loop = 'loop',
  autoplay = 'autoplay',
  muted = 'muted',
}

type VideoSettingsType = {
  [key in VideoSettings]?: boolean;
};

export const useVideoWidgetMutations = (
  sectionId: string,
  columnIndex: number,
  widgetId?: string,
) => {
  const guideId = useGuideStore(s => s.activeGuideId) as string;
  const pageId = useGuideStore(s => s.activePageId) as string;
  const chapterId = useGuideStore(s => s.activeChapterId) as string;
  const guideVersion = useGuideStore(s => s.activeGuideVersion) as number;

  return {
    toggleVideoWidgetSettings: useMutation(
      (option: VideoSettingsType) => {
        const optionKey = Object.keys(option)[0];
        const command = getCommand(optionKey as VideoSettings);
        return toggleVideoWidgetSettings({
          guideId,
          chapterId,
          pageId,
          sectionId,
          widgetId: widgetId ?? '',
          columnIndex,
          widgetType: WIDGET_TYPE.VIDEO,
          content: option,
          guideVersion,
          command,
        });
      },
      {
        onMutate: variables => {
          updateGuideVersion(guideId);
          optimisticallyUpdateVideoWidget(
            guideId,
            pageId,
            sectionId,
            columnIndex,
            widgetId ?? '',
            variables,
          );
        },
        onSuccess: data => {
          handleMutationSuccess('Update Video Widget Settings', data, {
            entityId: guideId,
          });
        },
      },
    ),
    editStyle: useMutation(
      (options: { style: string }) => {
        const command = editVideoWidgetStyleCommand({
          aggregateId: guideId,
          aggregateVersion: guideVersion,
          chapterId,
          sectionId,
          columnIndex,
          widgetId,
          pageId,
          widgetStyle: {
            stylesheet: options.style,
            language: 'css',
            version: 3,
          } as StyleSheet,
        });
        return guideWriteCommandApi(command);
      },
      {
        onMutate: variables => {
          updateGuideVersion(guideId);
          optimisticallyUpdateWidgetStyle(
            guideId,
            pageId,
            sectionId,
            columnIndex,
            widgetId,
            variables.style,
          );
        },
        onSuccess: data => {
          handleMutationSuccess(null, data, {
            entityId: guideId,
          });
        },
      },
    ),
    updateVideoWidget: useMutation(
      (options: {
        assetId: string;
        assetName: string;
        detailUrl: string;
        videoUrl: string;
        coverUrl: string;
      }) => {
        let wgtId = widgetId;

        if (!wgtId) {
          wgtId = sessionStorage.getItem('newWidgetId') ?? '';
        }

        return placeAssetInVideoWidget(
          guideId,
          chapterId,
          pageId,
          sectionId,
          wgtId,
          WIDGET_TYPE.VIDEO,
          {
            widgetAssetId: options.assetId,
            widgetAssetName: options.assetName,
            widgetVideoUrl: options.videoUrl,
            widgetDetailUrl: options.detailUrl,
            widgetCoverUrl: options.coverUrl,
          },
          guideVersion,
        );
      },
      {
        onMutate: variables => {
          let wgtId = widgetId;

          if (!wgtId) {
            wgtId = sessionStorage.getItem('newWidgetId') ?? '';
          }

          updateGuideVersion(guideId);
          optimisticallyUpdateVideoWidget(
            guideId,
            pageId,
            sectionId,
            columnIndex,
            wgtId,
            variables,
          );
        },
        onSuccess: data => {
          handleMutationSuccess('Update Video Widget', data, {
            entityId: guideId,
          });
        },
      },
    ),
  };
};

const optimisticallyUpdateVideoWidget = (
  guideId: string,
  pageId: string,
  sectionId: string,
  columnIndex: number,
  widgetId: string,
  variables?: {
    assetId?: string;
    assetName?: string;
    detailUrl?: string;
    videoUrl?: string;
    coverUrl?: string;
    controls?: boolean;
    style?: StyleSheet;
  },
) => {
  queryClient.setQueryData(
    [GUIDE_KEYS.PAGE_BY_ID, guideId, pageId],
    (old: Page) => {
      const sectionIndex = findIndex(
        old.sections,
        section => section.id === sectionId,
      );
      const widgetIndex = findIndex(
        old.sections[sectionIndex].columns[columnIndex].widgets,
        widget => widget.id === widgetId,
      );
      const newWidget = {
        ...old.sections[sectionIndex].columns[columnIndex].widgets[widgetIndex],
        ...variables,
      };

      if (old) {
        return produce(old, draft => {
          draft.sections[sectionIndex].columns[columnIndex].widgets[
            widgetIndex
          ] = newWidget;
        });
      }

      return old;
    },
  );
};

const getCommand = (optionKey: VideoSettings) => {
  switch (optionKey) {
    case VideoSettings.autoplay:
      return Command.EDIT_AUTOPLAY_IN_VIDEO_WIDGET;
    case VideoSettings.controls:
      return Command.EDIT_CONTROLS_IN_VIDEO_WIDGET;
    case VideoSettings.loop:
      return Command.EDIT_LOOP_IN_VIDEO_WIDGET;
    case VideoSettings.muted:
      return Command.EDIT_MUTED_IN_VIDEO_WIDGET;
    default:
      captureEvent(new Error('Unknown video setting'));
      return Command.EDIT_CONTROLS_IN_VIDEO_WIDGET;
  }
};
